import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');
    if (stored && token) {
      setUser(JSON.parse(stored));
      setImpersonating(!!localStorage.getItem('adminAccessToken'));
      api.get('/auth/me')
        .then((res) => {
          setUser(res.data.user);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        })
        .catch(() => logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Staff password login
  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', res.data.accessToken);
    localStorage.setItem('refreshToken', res.data.refreshToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setImpersonating(false);
    return res.data.user;
  };

  // Student OTP login
  const loginWithOtp = async (email, code) => {
    const res = await api.post('/auth/otp/verify', { email, code });
    localStorage.setItem('accessToken', res.data.accessToken);
    localStorage.setItem('refreshToken', res.data.refreshToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setImpersonating(false);
    return res.data.user;
  };

  // Admin impersonates a staff user — saves admin tokens aside, loads target user's tokens
  const impersonate = async (targetUserId) => {
    const res = await api.post(`/auth/impersonate/${targetUserId}`);
    // Stash admin credentials so we can restore them
    localStorage.setItem('adminAccessToken',  localStorage.getItem('accessToken'));
    localStorage.setItem('adminRefreshToken', localStorage.getItem('refreshToken'));
    localStorage.setItem('adminUser',         localStorage.getItem('user'));
    // Switch to impersonated user
    localStorage.setItem('accessToken',  res.data.accessToken);
    localStorage.setItem('refreshToken', res.data.refreshToken);
    localStorage.setItem('user',         JSON.stringify(res.data.user));
    setUser(res.data.user);
    setImpersonating(true);
  };

  // Restore the original admin session
  const stopImpersonating = () => {
    const adminToken   = localStorage.getItem('adminAccessToken');
    const adminRefresh = localStorage.getItem('adminRefreshToken');
    const adminUser    = localStorage.getItem('adminUser');
    if (!adminToken || !adminUser) return logout();
    localStorage.setItem('accessToken',  adminToken);
    localStorage.setItem('refreshToken', adminRefresh);
    localStorage.setItem('user',         adminUser);
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    setUser(JSON.parse(adminUser));
    setImpersonating(false);
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    setUser(null);
    setImpersonating(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, loginWithOtp, logout, loading, impersonating, impersonate, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
